import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSchema } from '../hooks/useSchema';
import { useAuth } from '../auth/AuthContext';

// SVG Icons for views and tables (simple white icons)
const icons = {
  // Timeline icon - calendar with clock
  timeline: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="9" y1="2" x2="9" y2="6" />
      <line x1="15" y1="2" x2="15" y2="6" />
      <circle cx="12" cy="15" r="3" />
      <line x1="12" y1="14" x2="12" y2="15.5" />
      <line x1="12" y1="15" x2="13" y2="15" />
    </svg>
  ),
  // Status icon - clipboard with checkmark
  status: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 14l2 2 4-4" />
    </svg>
  ),
  // Transfer icon - arrows between boxes
  transfer: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="6" width="6" height="6" rx="1" />
      <rect x="16" y="12" width="6" height="6" rx="1" />
      <path d="M8 9h4m0 0l-2-2m2 2l-2 2" />
      <path d="M16 15h-4m0 0l2-2m-2 2l2 2" />
    </svg>
  ),
  // Patient icon - person with medical cross
  patient: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="7" r="4" />
      <path d="M5 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2" />
      <line x1="18" y1="8" x2="18" y2="14" />
      <line x1="15" y1="11" x2="21" y2="11" />
    </svg>
  ),
  // Default view icon - chart/analytics
  view: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 3v18h18" />
      <path d="M7 16l4-4 4 4 5-6" />
    </svg>
  ),
  // Samples table icon - test tube
  samples: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M9 3v2m6-2v2" />
      <path d="M8 5h8l-1 14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2L8 5z" />
      <path d="M8 12h8" />
    </svg>
  ),
  // Subjects table icon - people
  subjects: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="9" cy="7" r="3" />
      <path d="M3 19v-1a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v1" />
      <circle cx="17" cy="7" r="2" />
      <path d="M21 19v-1a3 3 0 0 0-2-2.8" />
    </svg>
  ),
  // Organizations table icon - building
  organizations: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="4" y="4" width="16" height="16" rx="1" />
      <line x1="9" y1="4" x2="9" y2="20" />
      <line x1="15" y1="4" x2="15" y2="20" />
      <line x1="4" y1="9" x2="20" y2="9" />
      <line x1="4" y1="14" x2="20" y2="14" />
    </svg>
  ),
  // Files table icon - document
  files: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
    </svg>
  ),
  // Dependencies table icon - network/links
  dependencies: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="6" cy="6" r="3" />
      <circle cx="18" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="18" r="3" />
      <line x1="9" y1="6" x2="15" y2="6" />
      <line x1="6" y1="9" x2="6" y2="15" />
      <line x1="18" y1="9" x2="18" y2="15" />
      <line x1="9" y1="18" x2="15" y2="18" />
    </svg>
  ),
  // Analysis runs table icon - brain/processing
  analysis: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 6v6l4 2" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  ),
  // Sample versions icon - history/clock
  versions: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="9" />
      <polyline points="12,6 12,12 16,14" />
      <path d="M3 12a9 9 0 0 1 9-9" />
      <path d="M3 6v6h6" />
    </svg>
  ),
  // Source documents icon - inbox/email
  source: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  ),
  // Case ID sequences icon - hash/number
  sequences: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <line x1="4" y1="9" x2="20" y2="9" />
      <line x1="4" y1="15" x2="20" y2="15" />
      <line x1="10" y1="3" x2="8" y2="21" />
      <line x1="16" y1="3" x2="14" y2="21" />
    </svg>
  ),
  // Field config icon - sliders/settings
  config: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <line x1="4" y1="21" x2="4" y2="14" />
      <line x1="4" y1="10" x2="4" y2="3" />
      <line x1="12" y1="21" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12" y2="3" />
      <line x1="20" y1="21" x2="20" y2="16" />
      <line x1="20" y1="12" x2="20" y2="3" />
      <circle cx="4" cy="12" r="2" />
      <circle cx="12" cy="10" r="2" />
      <circle cx="20" cy="14" r="2" />
    </svg>
  ),
  // Migrations icon - database with arrow
  migrations: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
      <path d="M12 15v4m0 0l-2-2m2 2l2-2" />
    </svg>
  ),
  // Default table icon - database
  table: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
    </svg>
  ),
};

// Get icon for a view name
function getViewIcon(viewName: string): JSX.Element {
  if (viewName === 'vw_master_events_timeline') return icons.timeline;
  if (viewName === 'vw_sample_status') return icons.status;
  if (viewName === 'vw_outstanding_transfers') return icons.transfer;
  if (viewName === 'vw_sampling_patient') return icons.patient;
  return icons.view;
}

// Get icon for a table name
function getTableIcon(tableName: string): JSX.Element {
  if (tableName === 'samples') return icons.samples;
  if (tableName === 'subjects') return icons.subjects;
  if (tableName === 'organizations') return icons.organizations;
  if (tableName === 'sample_files') return icons.files;
  if (tableName === 'sample_dependencies') return icons.dependencies;
  if (tableName === 'analysis_runs') return icons.analysis;
  if (tableName === 'sample_versions') return icons.versions;
  if (tableName === 'source_documents') return icons.source;
  if (tableName === 'case_id_sequences') return icons.sequences;
  if (tableName === 'sample_field_config') return icons.config;
  if (tableName === '_migrations_history') return icons.migrations;
  return icons.table;
}

// Table category definitions
type TableCategory = {
  id: string;
  label: string;
  description: string;
  tables: string[];
  collapsed?: boolean;
};

const TABLE_CATEGORIES: TableCategory[] = [
  {
    id: 'core',
    label: 'Core Entities',
    description: 'Primary data tables',
    tables: ['samples', 'subjects', 'organizations'],
  },
  {
    id: 'sample-data',
    label: 'Sample Data',
    description: 'Extensions and relationships',
    tables: ['sample_files', 'sample_dependencies', 'sample_versions'],
  },
  {
    id: 'analysis',
    label: 'Analysis',
    description: 'AI processing and results',
    tables: ['analysis_runs'],
  },
  {
    id: 'system',
    label: 'System',
    description: 'Admin and configuration',
    tables: ['source_documents', 'case_id_sequences', 'sample_field_config', '_migrations_history'],
    collapsed: true,
  },
];

export function ViewSelector() {
  const navigate = useNavigate();
  const { schema, loading, error } = useSchema();
  const { isAdmin } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    new Set(TABLE_CATEGORIES.filter(c => c.collapsed).map(c => c.id))
  );

  // Filter categories based on user role - System tables only visible to admins
  const visibleCategories = TABLE_CATEGORIES.filter(c => c.id !== 'system' || isAdmin);

  const toggleCategory = (categoryId: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  // Filter tables and views based on search
  const filteredTables = schema?.tables.filter(t =>
    t.tableName.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];
  const filteredViews = schema?.views.filter(v =>
    v.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const handleViewClick = (tableName: string) => {
    navigate(`/tables/${tableName}`);
  };

  if (loading) {
    return (
      <div className="view-selector">
        <div className="loading">Loading available views...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="view-selector">
        <div className="error">Failed to load views: {error}</div>
      </div>
    );
  }

  return (
    <div className="view-selector">
      <div className="view-selector-header">
        <h1>Violet Research Samples</h1>
        <p className="text-muted">Select a view to explore your data</p>

        <div className="search-box">
          <input
            type="text"
            placeholder="Search tables and views..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Views Section - Primary Focus */}
      {filteredViews.length > 0 && (
        <section className="views-section">
          <h2>Views</h2>
          <div className="view-grid">
            {filteredViews.map((viewName) => {
              const isTimeline = viewName === 'vw_master_events_timeline';
              return (
                <div
                  key={viewName}
                  className={`view-card ${isTimeline ? 'view-card-featured' : ''}`}
                  onClick={() => handleViewClick(viewName)}
                >
                  <div className="view-card-icon">
                    {getViewIcon(viewName)}
                  </div>
                  <h3>{formatViewName(viewName)}</h3>
                  <p className="text-muted">{getViewDescription(viewName)}</p>
                  {isTimeline && <span className="badge badge-primary">Timeline</span>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Tables Section - Organized by Category */}
      {filteredTables.length > 0 && (
        <section className="tables-section">
          <h2>Base Tables</h2>
          {visibleCategories.map((category) => {
            const categoryTables = filteredTables.filter(t =>
              category.tables.includes(t.tableName)
            );
            if (categoryTables.length === 0) return null;

            const isCollapsed = collapsedCategories.has(category.id);

            return (
              <div key={category.id} className="table-category">
                <button
                  className="table-category-header"
                  onClick={() => toggleCategory(category.id)}
                  aria-expanded={!isCollapsed}
                >
                  <span className="table-category-toggle">
                    {isCollapsed ? '▶' : '▼'}
                  </span>
                  <span className="table-category-label">{category.label}</span>
                  <span className="table-category-description text-muted">
                    {category.description}
                  </span>
                  <span className="table-category-count text-muted">
                    {categoryTables.length}
                  </span>
                </button>
                {!isCollapsed && (
                  <div className="view-grid">
                    {categoryTables.map((table) => (
                      <div
                        key={table.tableName}
                        className="view-card"
                        onClick={() => handleViewClick(table.tableName)}
                      >
                        <div className="view-card-icon">
                          {getTableIcon(table.tableName)}
                        </div>
                        <h3>{formatTableName(table.tableName)}</h3>
                        <p className="text-muted">{getTableDescription(table.tableName)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </section>
      )}

      {filteredTables.length === 0 && filteredViews.length === 0 && (
        <div className="text-muted" style={{ textAlign: 'center', marginTop: '2rem' }}>
          No tables or views found matching "{searchTerm}"
        </div>
      )}
    </div>
  );
}

// Helper to format view names nicely
function formatViewName(name: string): string {
  return name
    .replace(/^vw_/, '')
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Helper to format table names nicely
function formatTableName(name: string): string {
  return name
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Helper to provide descriptions for known views
function getViewDescription(viewName: string): string {
  const descriptions: Record<string, string> = {
    'vw_master_events_timeline': 'Chronological timeline of all sample events and procedures',
    'vw_sample_status': 'Currently active samples and their status',
    'vw_outstanding_transfers': 'Pending sample transfers and shipments',
    'vw_sampling_patient': 'Patient sampling procedures and EEG monitoring',
  };
  return descriptions[viewName] || 'View data from this perspective';
}

// Helper to provide descriptions for known tables
function getTableDescription(tableName: string): string {
  const descriptions: Record<string, string> = {
    'samples': 'Core sample records with all event details',
    'subjects': 'Patient/subject information',
    'organizations': 'Labs, clinics, vendors, and other entities',
    'sample_files': 'Attachments and documents linked to samples',
    'sample_dependencies': 'Relationships and dependencies between samples',
    'sample_versions': 'Version history and change tracking',
    'analysis_runs': 'AI analysis results and processing history',
    'source_documents': 'Email ingestion and document sources',
    'case_id_sequences': 'Auto-generated vendor case ID sequences',
    'sample_field_config': 'Field behavior configuration for versioning',
    '_migrations_history': 'Database migration tracking',
  };
  return descriptions[tableName] || 'Raw table data';
}
